import React, { useMemo } from 'react'

import {
  BCKS_ADMIN,
  BCKS_GTK,
  BCKS_REGION_PREFIX,
  BCKS_SUPERADMIN,
  BCKS_UPT_PREFIX,
  KEMENDAGRI,
  ROLE_DETAILS,
  ROLES_ALLOWED_CHANGE_REGION,
  ROLES_SORTED_BY_PRIORITY,
} from 'configs/roles'

import { useAuth } from 'utils/hooks/useAuth'

export const useRoles = () => {
  const { bcksUserSession } = useAuth()
  const currentRoles: string[] = React.useMemo(() => {
    return (
      bcksUserSession?.user?.groups
        .map((group) => group.name)
        .sort(
          (roleA, roleB) =>
            ROLES_SORTED_BY_PRIORITY.findIndex((role) =>
              role.startsWith(roleA)
            ) -
            ROLES_SORTED_BY_PRIORITY.findIndex((role) => role.startsWith(roleB))
        ) ?? []
    )
  }, [bcksUserSession])

  const activeRole = useMemo(
    () =>
      currentRoles.filter((role) =>
        ROLE_DETAILS.some((rd) => role.startsWith(rd.key))
      )[0],
    [currentRoles]
  )

  const activeRoleName = useMemo(
    () =>
      ROLE_DETAILS.find((rd) => activeRole?.startsWith(rd.key))?.name ??
      activeRole,
    [activeRole]
  )

  const userHasRole = React.useCallback(
    (role: string): boolean =>
      currentRoles.some((currentRole) => currentRole.startsWith(role)),
    [currentRoles]
  )

  const userHasOneOfRoles = React.useCallback(
    (roles: string[]) =>
      roles.some((role) =>
        currentRoles.some((currentRole) => currentRole.startsWith(role))
      ),
    [currentRoles]
  )

  const canChangeRegion = userHasOneOfRoles(ROLES_ALLOWED_CHANGE_REGION)

  const isSuperAdminUser = userHasRole(BCKS_SUPERADMIN)
  const isAdminUser = userHasRole(BCKS_ADMIN)
  const isGtkUser = userHasRole(BCKS_GTK)
  const isDinasUser = userHasRole(BCKS_REGION_PREFIX)
  const isAdminUPTUser = userHasRole(BCKS_UPT_PREFIX)
  const isKemendagriUser = userHasRole(KEMENDAGRI)

  const hasConflictingRoles =
    [isDinasUser, isGtkUser, isKemendagriUser].filter((truthy) => truthy)
      .length >= 2

  return {
    userHasRole,
    userHasOneOfRoles,
    canChangeRegion,
    activeRole,
    activeRoleName,
    userRoles: currentRoles,
    isSuperAdminUser,
    isAdminUser,
    isGtkUser,
    isDinasUser,
    isAdminUPTUser,
    isKemendagriUser,
    hasConflictingRoles,
  }
}
